import { Fragment, useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, Modal, Form, Layout, Input, Space, Radio, message, Typography, Switch, Alert } from 'antd';
import { pageFormItemWithoutLabelGridLayout } from '@/utils/property';
import { getQuestionsListApi, saveActivityregistrationsApi, updateActivityregistrationsApi, getActivityIdentitiesApi, activityregistrationsDetailApi } from '@services/Conference';
import './index.less';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';
import TemplateQuestionsDrawer from '../components/TemPlateQuestionsDrawer';
import PageHeader from '@components/PageHeader/PageHeader';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
const { Text } = Typography;

// 可拖拽组件
const SortableItem = ({ id, field, getQuestionType }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: `translate3d(${transform?.x ?? 0}px, ${transform?.y ?? 0}px, 0) scale(1)`,
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className="drag-form-item">
                <div className="registration-right-drag-form-head">
                    <div className="head-left">
                        <div className="question-title">{getQuestionType(field.name)}</div>
                        <Form.Item {...field} name={[field.name, 'required']} valuePropName="checked">
                            <Checkbox />
                        </Form.Item>
                        必填
                    </div>
                    <div className="head-right">
                        <Button type="link" {...listeners}>
                            <i className="bi bi-grip-vertical"></i>
                        </Button>
                    </div>
                </div>
                <div className="registration-question-content">
                    <span className="registration-question-index">{field.name + 1}.</span>
                    <Form.Item {...field} name={[field.name, 'content']} rules={[{ required: true, message: '请输入题目内容' }]}>
                        <Input placeholder="请输入题目内容" readOnly />
                    </Form.Item>
                </div>

                <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                        const isRadio = !!getFieldValue('registrationQuestions') && getFieldValue('registrationQuestions')[field.name]?.questionType === 'radio';
                        const isCheckbox = !!getFieldValue('registrationQuestions') && getFieldValue('registrationQuestions')[field.name]?.questionType === 'checkbox';
                        return (
                            (isRadio || isCheckbox) && (
                                <Form.List name={[field.name, 'options']}>
                                    {(optionFields, { add, remove }) => (
                                        <>
                                            {optionFields.map((optionField, optionIndex) => (
                                                <div key={optionField.key}>
                                                    <div className="registration-right-drag-option-head">
                                                        {isRadio && <Radio disabled />}
                                                        {isCheckbox && <Checkbox disabled />}
                                                        <Form.Item {...optionField} name={[optionField.name, 'content']} rules={[{ required: true, message: '请输入选项内容' }]} style={{ width: 'calc(100% - 70px)' }}>
                                                            <Input placeholder="请输入选项内容" readOnly />
                                                        </Form.Item>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </Form.List>
                            )
                        );
                    }}
                </Form.Item>
            </div>
        </div>
    );
};

export default function SignUpTemplate() {
    const navigate = useNavigate();
    const params = useParams();

    const conferenceId = parseInt(params?.id) || 0; // 会议项目 ID
    const registrationId = parseInt(params?.templateId) || 0; // 报名模板 ID

    const [form] = Form.useForm();

    const [registration, setRegistration] = useState(null); // 报名模板信息

    const [title, setTitle] = useState(undefined);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [questionsList, setQuestionsList] = useState([]);
    const [activityIdentities, setActivityIdentities] = useState([]);

    const questions = Form.useWatch('regQuestions', form);

    // 提交
    const onFinish = () => {
        form.validateFields().then(values => {
            // antd v6 validateFields 不包含 Form.List 未注册子项（如 id），从 store 取完整值
            const registrationQuestions = form.getFieldValue('registrationQuestions') || [];

            // 根据认证资料是否必填设置 authFieldsSettings
            const authFieldsSettings = values.authFieldsRequired ? { mem_identity_path_enabled: true, mem_identity_path_required: true, person_auth_fields_enabled: true } : { mem_identity_path_enabled: true, person_auth_fields_enabled: true };

            const params = {
                id: form.getFieldValue('id') || undefined,
                activityId: conferenceId,
                activityType: 'conference',
                name: values.name,
                regContacts: JSON.stringify(values.regContacts),
                regIdentities: values.regIdentities.join(),
                regQuestions: JSON.stringify(registrationQuestions.map(item => ({ questionId: item.id, required: item.required ? 1 : 0 }))),
                authFieldsSettings: JSON.stringify(authFieldsSettings),
            };
            const reqApi = form.getFieldValue('id') ? updateActivityregistrationsApi : saveActivityregistrationsApi;
            reqApi(params).then(res => {
                if (res.status_code === 200) {
                    message.success('保存成功');
                    navigate(`/conf/conferences/${conferenceId}/signup`);
                }
            });
        });
    };

    // 根据模板id获取详情
    const activityregistrationsDetail = async () => {
        const res = await activityregistrationsDetailApi({ registrationId: registrationId });
        if (res && res.status_code === 200 && res.data) {
            setRegistration(res.data);
            getQuestionsList('edit', res.data);
        }
    };

    const onCancel = () => {
        Modal.confirm({
            title: '提示',
            content: '可能有数据变动，确认不保存？',
            cancelText: '取消',
            okText: '确认',
            closable: true,
            onOk: () => navigate(`/conf/conferences/${conferenceId}/signup`),
        });
    };

    const goBack = () => {
        Modal.confirm({
            title: '提示',
            content: '可能有数据变动，确认不保存？',
            cancelText: '取消',
            okText: '确认',
            closable: true,
            onOk: () => navigate(`/conf/conferences/${conferenceId}/signup`),
        });
    };

    // 处理拖拽结束
    const handleDragEnd = event => {
        const { active, over } = event;
        const registrationQuestions = form.getFieldValue('registrationQuestions');
        if (over && active.id !== over.id) {
            const oldIndex = registrationQuestions.findIndex((_, index) => index === active?.data?.current?.sortable?.index);
            const newIndex = registrationQuestions.findIndex((_, index) => index === over?.data?.current?.sortable?.index);
            const newFields = arrayMove(registrationQuestions, oldIndex, newIndex);
            form.setFieldValue('registrationQuestions', newFields);
            // 拖拽 - 同步更新左侧已选题目id顺序
            form.setFieldValue(
                'regQuestions',
                newFields.map(item => item.id),
            );
        }
    };

    // 题目类型名称
    const getQuestionType = index => {
        const list = form.getFieldValue('registrationQuestions') || [];
        if (list[index]) {
            if (list[index].questionType === 'radio') {
                return '单选题';
            }
            if (list[index].questionType === 'checkbox') {
                return '多选题';
            }
            if (list[index].questionType === 'text') {
                return '问答题';
            }
        }
        return '';
    };

    // 题目类型名称
    const getTitleType = type => {
        if (type === 'radio') {
            return '单选题';
        }
        if (type === 'checkbox') {
            return '多选题';
        }
        if (type === 'text') {
            return '问答题';
        }
    };

    const handleOpenQuestions = () => {
        setOpenDrawer(true);
    };

    // 报名身份列表
    const getActivityIdentities = () => {
        getActivityIdentitiesApi({ activityId: conferenceId, activityType: 'conference' }).then(res => {
            if (res && res.status_code === 200) {
                setActivityIdentities(res.data);
            }
        });
    };

    // 可选问题列表
    const getQuestionsList = (type, detailInfo) => {
        getQuestionsListApi({ activityId: conferenceId, activityType: 'conference' }).then(res => {
            if (res && res.status_code === 200) {
                if (type) {
                    const list = res.data;
                    let newList = [];

                    const { id, name, regContacts, regIdentities, regQuestions, authFieldsSettings } = detailInfo;
                    const regContactsArr = JSON.parse(regContacts || '[]');
                    const regIdentitiesArr = regIdentities.split(',').map(item => Number(item));
                    const regQuestionsArr = JSON.parse(regQuestions || '[]').map(item => item.questionId);

                    // 解析authFieldsSettings来确定认证资料是否必填
                    let authFieldsRequired = true;
                    if (authFieldsSettings) {
                        try {
                            const settings = JSON.parse(authFieldsSettings);
                            authFieldsRequired = settings.mem_identity_path_required === true;
                        } catch (e) {
                            authFieldsRequired = true;
                        }
                    }
                    let registrationQuestionsArr = [];

                    // 前端页面存在缓存数据时 - 同步页面实时更新 - 信息收集配置
                    const registrationQuestions = form.getFieldValue('registrationQuestions');
                    if (registrationQuestions && !!registrationQuestions.length) {
                        if (list && !!list.length) {
                            // 回显 - 已选题目
                            registrationQuestionsArr = registrationQuestions.filter(item => list.some(i => i.id === item.id));
                            setQuestionsList(list);
                        }
                        form.setFieldsValue({
                            id,
                            name,
                            regContacts: regContactsArr,
                            regIdentities: regIdentitiesArr,
                            regQuestions: registrationQuestionsArr.map(item => item.id),
                            registrationQuestions: registrationQuestionsArr,
                            authFieldsRequired,
                        });
                    } else {
                        if (list && !!list.length) {
                            // 回显 - 已选题目
                            registrationQuestionsArr = JSON.parse(regQuestions || '[]').map(item => {
                                const target = list.find(i => i.id === item.questionId);
                                return { ...target, required: !!item.required };
                            });

                            // 同步题目列表的必填状态
                            newList = list.map(item => {
                                const target = JSON.parse(regQuestions || '[]').find(i => i.questionId === item.id);
                                return { ...item, required: target ? target.required : item.required };
                            });
                            setQuestionsList(newList);
                        }
                        form.setFieldsValue({
                            id,
                            name,
                            regContacts: regContactsArr,
                            regIdentities: regIdentitiesArr,
                            regQuestions: regQuestionsArr,
                            registrationQuestions: registrationQuestionsArr,
                            authFieldsRequired,
                        });
                    }
                } else {
                    setQuestionsList(res.data);
                }
            }
        });
    };

    useEffect(() => {
        getActivityIdentities();

        // 新增模板时
        if (!registrationId) {
            getQuestionsList();
        }
        // 编辑模板时 - 先调取模板详情接口，再调取问题列表接口
        if (registrationId) {
            activityregistrationsDetail();
        }
    }, []);

    // 新增模板时 - 可选问题 - 同步右侧预览
    useEffect(() => {
        if (questions && !!questions.length) {
            const arr = [];
            // 过滤已删除的题目
            console.log(questions, questionsList);
            const newList = questions.filter(item => questionsList.find(i => i.id === item));

            const registrationQuestions = form.getFieldValue('registrationQuestions') || [];
            console.log('registrationQuestions', registrationQuestions);
            newList.forEach(item => {
                const target = questionsList.find(i => i.id === item);
                if (target) {
                    arr.push({ ...target, required: registrationQuestions.find(i => i.id === item) ? registrationQuestions.find(i => i.id === item).required : target.required }); //新增勾选 - 默认必填
                }
            });
            form.setFieldValue('registrationQuestions', arr);
        } else {
            form.setFieldValue('registrationQuestions', []);
        }
    }, [questions, questionsList]);

    useEffect(() => {
        document.title = registrationId ? '编辑报名模板-医会议' : '新增报名模板-医会议';
    }, [registrationId]);

    return (
        <Fragment>
            <PageHeader
                title={
                    <>
                        <i className="bi bi-chevron-left page-header-back-btn" onClick={() => navigate(`${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}/signup`)} />
                        <span>{registrationId ? '编辑报名模板' : '新增报名模板'}</span>
                    </>
                }
                breadcrumb={[
                    { label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH },
                    { label: title || '项目信息', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` },
                    { label: '报名签到', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}/signup` },
                    registrationId ? '编辑报名模板' : '新增报名模板',
                ]}
            />

            <div className="page-route-body signup-template-page">
                <div className="main-content-body">
                    <Form form={form} initialValues={{ authFieldsRequired: true }}>
                        <Form.Item label="模板名称" name="name" extra={<Text type="warning">模板名称仅后台展示，为方便模板复用请清晰填写报名模板名称</Text>} rules={[{ required: true, message: '请输入模板名称' }]}>
                            <Input placeholder="请输入模板名称" style={{ width: '400px' }} maxLength={50} showCount disabled={registrationId && registration?.createdBy === '系统'} />
                        </Form.Item>

                        <Form.Item label="可选报名身份" name="regIdentities" rules={[{ required: true, message: '请选择报名身份' }]} extra={<Text type="warning">用户在该报名模板中可选择的报名身份</Text>}>
                            <Checkbox.Group className="reg-identities-group">
                                {activityIdentities && activityIdentities.length
                                    ? activityIdentities.map(item => (
                                          <Checkbox key={item.id} value={item.id}>
                                              <div className="check-activity-identity">
                                                  <div className="check-activity-identity-name">{item.name}</div>
                                                  <div className="check-activity-identity-benefits">
                                                      {JSON.parse(item.benefits || '[]').map((i, idx) => (
                                                          <span key={idx} className="benefits-item">{`${i.name}*${i.amount}`}</span>
                                                      ))}
                                                  </div>
                                              </div>
                                          </Checkbox>
                                      ))
                                    : null}
                            </Checkbox.Group>
                        </Form.Item>

                        <Form.Item label="联系人" className="required">
                            <Form.List name="regContacts" initialValue={[{ name: '', phone: '' }]}>
                                {(fields, { add, remove }) => (
                                    <>
                                        <Form.Item>
                                            <Button ghost type="primary" onClick={() => add()} icon={<i className="bi bi-plus"></i>}>
                                                新增联系人
                                            </Button>
                                            <div>
                                                <Text type="warning">活动报名页，报名详情页展示</Text>
                                            </div>
                                        </Form.Item>
                                        {fields.map(({ key, name, ...restField }, index) => (
                                            <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                                <Form.Item {...restField} name={[name, 'name']} label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                                                    <Input placeholder="请输入姓名" maxLength={50} showCount />
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'phone']}
                                                    label="手机号"
                                                    rules={[
                                                        { required: true, message: '请填写手机号码' },
                                                        { pattern: /^1\d{10}$/, message: '请输入正确的手机号码' },
                                                    ]}
                                                >
                                                    <Input placeholder="请输入手机号" />
                                                </Form.Item>
                                                {key > 0 && (
                                                    <span className="anticon" onClick={() => remove(name)}>
                                                        <i className="bi bi-dash-circle"></i>
                                                    </span>
                                                )}
                                            </Space>
                                        ))}
                                    </>
                                )}
                            </Form.List>
                        </Form.Item>

                        <Form.Item label="信息收集配置" rules={[{ required: true, message: '信息收集配置' }]}>
                            <Text type="warning">用户报名时需要填写信息</Text>
                            <div className="registration-wrap">
                                <div className="registration-left">
                                    <div className="registration-left-info">
                                        <div className="registration-left-info-title">基本信息</div>
                                    </div>

                                    <Alert
                                        title={
                                            <>
                                                1. 本人报名时，系统将根据用户是否上传“认证资料（图片）”，自动判断是否为用户提交认证申请或认证资料修改。
                                                <br />
                                                2. 当用户打开报名页面时，若距离会议开始日期零点不足48小时，用户端“认证资料（图片）”字段，将按非必填设置展示。
                                            </>
                                        }
                                        type="warning"
                                        style={{ margin: '8px 0' }}
                                    />

                                    <div className="registration-left-checkbox">
                                        <Checkbox defaultChecked disabled>
                                            您的身份
                                        </Checkbox>
                                        <br />
                                        <Checkbox defaultChecked disabled>
                                            姓名
                                        </Checkbox>
                                        <br />
                                        <Checkbox defaultChecked disabled>
                                            单位
                                        </Checkbox>
                                        <br />
                                        <Checkbox defaultChecked disabled>
                                            科室
                                        </Checkbox>
                                        <br />
                                        <Checkbox defaultChecked disabled>
                                            职务
                                        </Checkbox>
                                        <div className="cert-image">
                                            <Checkbox defaultChecked disabled>
                                                认证资料（图片）
                                            </Checkbox>
                                            <span>必填：</span>
                                            <Form.Item name="authFieldsRequired" valuePropName="checked" noStyle>
                                                <Switch checkedChildren="是" unCheckedChildren="否" />
                                            </Form.Item>
                                        </div>
                                    </div>
                                    <div className="registration-left-add">
                                        <div className="registration-left-add-title">
                                            <span>可选问题</span>
                                            <Button type="primary" onClick={handleOpenQuestions}>
                                                可选问题管理
                                            </Button>
                                        </div>
                                        <Form.Item name="regQuestions">
                                            <Checkbox.Group
                                                options={
                                                    questionsList && questionsList.length
                                                        ? questionsList.map(item => ({
                                                              value: item.id,
                                                              label: `${item.content}（${getTitleType(item.questionType)}）`,
                                                          }))
                                                        : []
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                <div className="registration-right">
                                    <div className="registration-right-drag-form scrollable-container">
                                        <div className="registration-right-drag-form-title">已选问题</div>
                                        <DndContext onDragEnd={handleDragEnd}>
                                            <Form.List name="registrationQuestions">
                                                {(fields, { remove }) => (
                                                    <SortableContext items={fields.map(field => field.key)}>
                                                        {fields.map((field, index) => (
                                                            <SortableItem key={field.key} id={field.key} field={field} remove={remove} getQuestionType={getQuestionType} />
                                                        ))}
                                                    </SortableContext>
                                                )}
                                            </Form.List>
                                        </DndContext>
                                    </div>
                                </div>
                            </div>
                        </Form.Item>

                        <div className="page-form-foot-operations">
                            <Space size="large">
                                <Button type="primary" size="large" htmlType="submit" onClick={onFinish}>
                                    提交
                                </Button>
                                <Button type="default" size="large" onClick={onCancel}>
                                    取消
                                </Button>
                            </Space>
                        </div>
                    </Form>
                </div>
            </div>

            <TemplateQuestionsDrawer openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} updateList={registrationId ? activityregistrationsDetail : getQuestionsList} />
        </Fragment>
    );
}
